import * as THREE from 'three';
import { clamp, type PhysicsWorld } from '@kokun/shared';
import { settings } from '../../app/settings';

/**
 * Cámara en tercera persona: órbita con límites de inclinación, zoom, seguimiento suave y
 * colisión con el escenario (rayo contra las cajas del mundo) para no atravesar paredes.
 */
export class ThirdPersonCamera {
  yaw = 0;
  pitch = 0.32;
  distance = 6.5;
  private targetDistance = 6.5;
  private current = new THREE.Vector3();
  private focus = new THREE.Vector3();
  private shake = 0;
  private initialized = false;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private world: PhysicsWorld | null,
  ) {}

  setWorld(w: PhysicsWorld) {
    this.world = w;
  }

  look(dx: number, dy: number) {
    const s = settings.get();
    const k = 0.0025 * s.sensitivity;
    this.yaw -= dx * k;
    this.pitch = clamp(this.pitch + dy * k * (s.invertY ? -1 : 1), -0.35, 1.25);
  }

  zoom(dir: number) {
    this.targetDistance = clamp(this.targetDistance + dir * 0.8, 2.5, 12);
  }

  addShake(amount: number) {
    if (!settings.get().reduceEffects) this.shake = Math.min(1, this.shake + amount);
  }

  snap() {
    this.initialized = false;
  }

  update(target: THREE.Vector3, dt: number) {
    this.distance += (this.targetDistance - this.distance) * Math.min(1, dt * 6);
    const head = new THREE.Vector3(target.x, target.y + 1.6, target.z);
    if (!this.initialized) this.focus.copy(head);
    this.focus.lerp(head, Math.min(1, dt * 14));

    const cp = Math.cos(this.pitch);
    const dir = new THREE.Vector3(Math.sin(this.yaw) * cp, Math.sin(this.pitch), Math.cos(this.yaw) * cp);
    let dist = this.distance;
    if (this.world) {
      const hit = this.world.raycast(this.focus.x, this.focus.y, this.focus.z, dir.x, dir.y, dir.z, dist + 0.3, true);
      if (hit !== null) dist = Math.max(0.8, hit - 0.35);
    }
    const desired = this.focus.clone().addScaledVector(dir, dist);
    if (desired.y < 0.3) desired.y = 0.3;
    if (!this.initialized) {
      this.current.copy(desired);
      this.initialized = true;
    }
    // Acercarse rápido (evitar paredes) y alejarse suave
    const closer = desired.distanceTo(this.focus) < this.current.distanceTo(this.focus);
    this.current.lerp(desired, Math.min(1, dt * (closer ? 25 : 8)));
    this.camera.position.copy(this.current);
    if (this.shake > 0) {
      const a = this.shake * 0.25;
      this.camera.position.x += (Math.random() - 0.5) * a;
      this.camera.position.y += (Math.random() - 0.5) * a;
      this.shake = Math.max(0, this.shake - dt * 2.5);
    }
    this.camera.lookAt(this.focus);
  }
}
